import { useCallback, useEffect, useState } from "react"

import styles from "./index.module.scss"
import ConfigIcon from "../../../assets/icons/ConfigIcon"
import RcmpLoading from "../../atoms/Loading"
import RcmpHeader from "../../atoms/Header"
import ContentBlank from "../../molecules/ContentBlank"
import Button from "../../atoms/Button"
import { useNavigate } from "react-router-dom"
import { fnBisAdmin } from "../../../utils/permissions"
import { getExtract } from "../../../services/plan"
import { getUser } from "../../../services/user"
import { toast } from "@stardust-ds/react"
import { fnDecodeStorageToken } from "../../../utils/encodeLocalStorage"
import CreditCardDisplay from "./components/CreditCardDisplay"
import intl from "react-intl-universal"
import { useAppearanceContext } from "../../../contexts/Appearance"

export default function ExtractPage() {
  const [bIsLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const { oConfig: oConfigWL } = useAppearanceContext()
  const [oBill, setBill] = useState({})
  const [oUser, setUser] = useState({})

  useEffect(() => {
    if (!fnBisAdmin() || oConfigWL === null || oConfigWL.nWlpnId) {
      navigate("./")
    }

    Promise.all([loadExtract(), loadUser()]).finally(() => {
      setIsLoading(false)
    })
  }, [])

  const loadExtract = useCallback(async () => {
    try {
      const oBill = await getExtract()
      setBill(oBill)
    } catch (oError) {
      //   toast({
      //     type: "error",
      //     description: oError.message,
      //   })
    }
  }, [])

  const loadUser = useCallback(async () => {
    const nUserId = fnDecodeStorageToken()?.nUserId
    try {
      const oUser = await getUser(nUserId)
      setUser(oUser)
    } catch (oError) {
      // toast({
      //   type: "error",
      //   description: oError.message,
      // })
    }
  }, [])

  const handlePlanButtonClick = useCallback(() => {
    navigate("/plan")
  }, [])

  const openExternalLink = useCallback((cUrl) => {
    window.open(cUrl)
  }, [])

  const convertSuperlogicaDate = (cDate = "") => {
    if (!cDate.length) {
      return "N/A"
    }
    const [month, day, year] = cDate.split("/")
    return `${day}/${month}/${year}`
  }

  const getDueDate = () => {
    if (oBill.nDueDay > 0) {
      const oDate = new Date(oBill.oInvoiceLast?.due_date || "")
      const aDueDate = oBill.oInvoiceLast?.due_date?.split("/") || []
      if (oBill.bIsAnnual) {
        oDate.setFullYear(Number(aDueDate[2]) + 1)
      } else {
        oDate.setMonth(Number(aDueDate[0]) % 12)
      }
      const cLocale = fnDecodeStorageToken()?.cLanguage ?? "pt-br"
      return oDate.toLocaleDateString(cLocale)
    }
    return "N/A"
  }

  const getPaymentMethod = () => {
    let oCurrBill
    if (oBill.oBillNext?.payment_method_name) {
      oCurrBill = oBill.oBillNext
    } else if (oBill.oInvoiceLast?.payment_method_name) {
      oCurrBill = oBill.oInvoiceLast
    }

    if (!oCurrBill) return "N/A"
    if (oCurrBill.payment_method_name === "Boleto") {
      return "Boleto"
    }
    return (
      <CreditCardDisplay
        cCreditCardNumber={oCurrBill.credit_card_number}
        cDueMonth={oCurrBill.credit_card_due_month}
        cDueYear={oCurrBill.credit_card_due_year}
      />
    )
  }

  const getFormattedMoney = (nValue) => {
    if (!nValue) return "R$ 0,00"
    const cLocale = fnDecodeStorageToken()?.cLanguage ?? "pt-br"
    return new Intl.NumberFormat(cLocale, {
      style: "currency",
      currency: "BRL",
    }).format(nValue)
  }

  return (
    <>
      <RcmpHeader
        cTitlePage={intl.get("plan_extract.page_title")}
        elIcon={<ConfigIcon />}
        className={styles.header}
        aBreadCrumbs={[]}
      />
      <ContentBlank
        bSwitchable={false}
        bDisableActions
        bCanShowActions={false}
        bShowFilter={false}
        bCanSearch={false}
      >
        {bIsLoading ? (
          <div className={styles.loadingContainer}>
            <RcmpLoading bShow={true} size={60} />
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.cardRow} style={{ padding: "0px 15px" }}>
              <p className={styles.title}>{intl.get("plan_extract.overview")}</p>
            </div>
            <div className={styles.cardRow}>
              <div className={styles.card}>
                <p>{intl.get("plan_extract.active_users")}</p>
                <p className={styles.planAmount}>{oBill.nTotalUsers ?? 0}</p>
                <Button variant="outline" onClick={handlePlanButtonClick}>
                  {intl.get("plan_extract.update_users")}
                </Button>
              </div>
              <div className={styles.card}>
                <p>{intl.get("plan_extract.hired_storage")}</p>
                <p className={styles.planAmount}>{oBill.nTotalSpace ?? 0} Gb</p>
                <Button variant="outline" onClick={handlePlanButtonClick}>
                  {intl.get("plan_extract.update_storage")}
                </Button>
              </div>
              <div className={styles.card}>
                {oBill.bIsAnnual ? (
                  <p>{intl.get("plan_extract.annual_plan")}</p>
                ) : (
                  <p>{intl.get("plan_extract.monthly_plan")}</p>
                )}
                <p className={styles.planAmount}>{getFormattedMoney(oBill.nPaidValue)}</p>
                <Button variant="outline" onClick={handlePlanButtonClick}>
                  {intl.get("plan_extract.manage_plan")}
                </Button>
              </div>
            </div>
            <div className={styles.cardRow}>
              <div className={`${styles.card} ${styles.alignLeft}`}>
                <p className={styles.title}>
                  {intl.get("plan_extract.last_paid_bill")}:{" "}
                  {oBill.oInvoiceLast?.id ? `#${oBill.oInvoiceLast.id}` : "N/A"}
                </p>
                <p>
                  {intl.get("plan_extract.value")}:{" "}
                  {oBill.oInvoiceLast?.total_value
                    ? "R$" + oBill.oInvoiceLast.total_value
                    : "N/A"}
                </p>
                <p>
                  {intl.get("plan_extract.payment_date")}:{" "}
                  {convertSuperlogicaDate(oBill.oInvoiceLast?.payment_date)}
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    openExternalLink(oBill.cSuperlogicaHistoryLink)
                  }}
                  disabled={!oBill.cSuperlogicaAreaLink}
                >
                  {intl.get("plan_extract.view_payment_history")}
                </Button>
              </div>
              <div className={`${styles.card} ${styles.alignLeft}`}>
                <p className={styles.title}>{intl.get("plan_extract.billing_details")}</p>
                <p>
                  {intl.get("plan_extract.company")}:{oUser.customer?.cust_cname ?? "N/A"}
                </p>
                <p>
                  {intl.get("plan_extract.payment_method")}:{getPaymentMethod()}
                </p>
                <p>
                  {intl.get("plan_extract.next_payment_date")}:
                  {oBill.oBillNext?.due_date
                    ? convertSuperlogicaDate(oBill.oBillNext.due_date)
                    : getDueDate()}
                </p>
                <p>
                  {intl.get("plan_extract.billing_country")}:{" "}
                  {oUser.customer?.cust_ccountry ?? "N/A"}
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    openExternalLink(oBill.cSuperlogicaAreaLink)
                  }}
                  disabled={!oBill.cSuperlogicaAreaLink}
                >
                  {intl.get("plan_extract.alter_data")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ContentBlank>
    </>
  )
}
